/*
Purpose: Guided configuration UI for setting up a bounty request (crowdsourced analysis).
Audience: Any user wanting to create a bounty for community analysis of evidence.
What it does:
- Presents targets (vehicles include plates), model choices, and reward amounts
- Estimates cost and validates configuration before posting
- Emits callbacks to create bounty request
Interplay:
- Reads centralized AI templates/models (frontend mirror of backend config)
- Used inside EvidenceModal to create bounty requests
- Works with bounty system for crowdsourced analysis
*/
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FaDollarSign, FaFileAlt, FaMicrophone, FaCar, FaUsers, FaPlay, FaCheckCircle, FaCog, FaBullhorn } from 'react-icons/fa';
import { BiTargetLock } from 'react-icons/bi';
import { getAvailableModels, mapModelToPreferredService, getModelConfig } from '../../config/aiModelsConfig';
import { useNotifications } from '../notifications/NotificationsProvider';
import '../../styles/BountyConfigurationMenu.css';

const BountyConfigurationMenu = ({ evidence, casefileId, onBountyCreated, onClose, currentUser }) => {
  const { notify } = useNotifications();
  const functions = getFunctions();
  
  // State management
  const [selectedTargets, setSelectedTargets] = useState(['vehicles']);
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash-lite');
  const [selectedTier, setSelectedTier] = useState(() => mapModelToPreferredService('gemini-2.5-flash-lite'));
  const [bountyAmount, setBountyAmount] = useState('');
  const [deadline, setDeadline] = useState('');
  const [costEstimate, setCostEstimate] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  
  // User preferences
  const userPreferences = {
    analysisDepth: 'Standard',
    detailedOutput: true,
    autoCorrelate: false,
    exportForCourt: true,
    confidenceThreshold: 0.8
  };

  // Analysis targets configuration
  const analysisTargets = useMemo(() => [
    { 
      id: 'vehicles', 
      name: 'Vehicles', 
      icon: FaCar, 
      description: 'Detect and track vehicles; make/model ID; license plates; marks', 
      cost: 0.0015,
      color: '#f59e0b'
    },
    { 
      id: 'people', 
      name: 'People', 
      icon: FaUsers, 
      description: 'Detect people, facial analysis, behavior tracking', 
      cost: 0.002,
      color: '#ef4444'
    },
    { 
      id: 'activities', 
      name: 'Activities', 
      icon: FaPlay, 
      description: 'Recognize actions, behaviors, and events', 
      cost: 0.003,
      color: '#8b5cf6'
    },
    { 
      id: 'text', 
      name: 'Text & OCR', 
      icon: FaFileAlt, 
      description: 'Extract text from images and documents', 
      cost: 0.001,
      color: '#06b6d4'
    },
    { 
      id: 'audio', 
      name: 'Audio Analysis', 
      icon: FaMicrophone, 
      description: 'Speech transcription and audio analysis', 
      cost: 0.006,
      color: '#ec4899'
    }
  ], []);

  // Load available models on mount
  useEffect(() => {
    setAvailableModels(getAvailableModels());
  }, []);

  // Cost estimation logic
  const updateCostEstimation = useCallback(() => {
    if (!evidence || selectedTargets.length === 0) {
      setCostEstimate(0);
      return;
    }

    const selectedTargetData = analysisTargets.filter(target => selectedTargets.includes(target.id));
    const baseCost = selectedTargetData.reduce((sum, target) => sum + target.cost, 0);
    const modelConfig = getModelConfig(selectedModel);
    const modelMultiplier = modelConfig?.cost_multiplier || 1;
    
    // Estimate based on evidence type and duration/size
    let complexityMultiplier = 1;
    if (evidence.fileType?.startsWith('video/')) {
      complexityMultiplier = Math.min(evidence.duration || 60, 300) / 60; // Cap at 5 minutes
    } else if (evidence.fileType?.startsWith('image/')) {
      complexityMultiplier = 0.5;
    }

    const totalCost = baseCost * modelMultiplier * complexityMultiplier;
    setCostEstimate(Math.max(totalCost, 0.01)); // Minimum cost
  }, [selectedTargets, selectedModel, evidence, analysisTargets]);

  // Update cost when dependencies change
  useEffect(() => {
    updateCostEstimation();
  }, [selectedTargets, selectedModel, evidence, updateCostEstimation]);

  // Handle target selection
  const handleTargetToggle = (targetId) => {
    setSelectedTargets(prev => 
      prev.includes(targetId) 
        ? prev.filter(id => id !== targetId)
        : [...prev, targetId]
    );
  };

  // Handle model selection
  const handleModelSelection = (modelId) => {
    setSelectedModel(modelId);
  };

  // Create bounty request
  const createBountyRequest = async () => {
    if (!bountyAmount || selectedTargets.length === 0) {
      notify({
        severity: 'error',
        title: 'Incomplete Bounty',
        message: 'Please enter a bounty amount and select at least one analysis target.'
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const createBounty = httpsCallable(functions, 'createBountyRequest');
      const bountyRequest = {
        evidenceId: evidence.id,
        casefileId: casefileId,
        targets: selectedTargets,
        selectedModel: selectedModel,
        preferredService: mapModelToPreferredService(selectedModel),
        bountyAmount: parseFloat(bountyAmount),
        deadline: deadline,
        analysisDepth: userPreferences.analysisDepth,
        customInstructions: `Bounty analysis requested by ${currentUser?.displayName || 'user'}. 
          ${userPreferences.detailedOutput ? 'Include detailed metadata and timestamps. ' : ''}
          ${userPreferences.autoCorrelate ? 'Cross-reference with existing case evidence. ' : ''}
          ${userPreferences.exportForCourt ? 'Generate court-ready documentation with chain of custody. ' : ''}
          Confidence threshold: ${userPreferences.confidenceThreshold * 100}%.`
      };

      const result = await createBounty(bountyRequest);
      
      if (result.data.success) {
        notify({
          severity: 'success',
          title: 'Bounty Created',
          message: `Your bounty has been posted successfully. Bounty ID: ${result.data.bountyId}`
        });
        
        if (onBountyCreated) {
          onBountyCreated(result.data);
        }
        
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error creating bounty:', error);
      notify({
        severity: 'error',
        title: 'Bounty Creation Failed',
        message: error.message || 'Could not create bounty request.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bounty-config-menu">
      {/* Header Section */}
      <div className="config-header">
        <BiTargetLock className="header-icon" style={{ color: '#ffc107' }} />
        <div className="header-text">
          <h3>Bounty Configuration</h3>
          <p>Configure bounty request for {evidence?.fileName}</p>
        </div>
      </div>

      {/* Analysis Targets Section */}
      <div className="config-section">
        <h4>Analysis Targets</h4>
        <div className="target-cards">
          {analysisTargets.map((target) => {
            const Icon = target.icon;
            const isSelected = selectedTargets.includes(target.id);
            
            return (
              <div
                key={target.id}
                className={`target-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleTargetToggle(target.id)}
              >
                <div className="card-header">
                  <Icon className="card-icon" />
                  <h5>{target.name}</h5>
                  {isSelected && <FaCheckCircle className="selected-icon" />}
                </div>
                <p>{target.description}</p>
                <div className="card-cost">${target.cost.toFixed(4)}/min</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Model Selection */}
      <div className="config-section">
        <h4>
          <FaCog />
          AI Model Selection
        </h4>
        {/* Tier Selector */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'google-vertex-vision-standard', label: 'Standard' },
            { id: 'google-vertex-vision-flash', label: 'Flash' },
            { id: 'google-vertex-vision-pro', label: 'Pro' }
          ].map(tier => (
            <button
              key={tier.id}
              onClick={() => {
                setSelectedTier(tier.id);
                if (tier.id !== 'all') {
                  // If current model not in this tier, auto-select first model in tier
                  const modelsInTier = availableModels.filter(m => mapModelToPreferredService(m.id) === tier.id);
                  if (!modelsInTier.some(m => m.id === selectedModel) && modelsInTier.length > 0) {
                    setSelectedModel(modelsInTier[0].id);
                  }
                }
              }}
              className={`bounty-tier-chip ${selectedTier === tier.id ? 'active' : ''}`}
            >
              {tier.label}
            </button>
          ))}
        </div>
        <div className="bounty-model-selection-grid">
          {availableModels
            .filter(model => selectedTier === 'all' || mapModelToPreferredService(model.id) === selectedTier)
            .map(model => {
            const isSelected = selectedModel === model.id;
            const modelTier = mapModelToPreferredService(model.id);
            
            return (
              <div
                key={model.id}
                className={`bounty-model-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleModelSelection(model.id)}
              >
                <div className="bounty-model-header">
                  <FaCog className="bounty-model-icon" />
                  <div className="bounty-model-info">
                    <h5>{model.name}</h5>
                    <p className="bounty-model-provider">{model.provider}</p>
                  </div>
                  <span className="tier-badge" style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#334155'
                  }}>
                    {modelTier === 'google-vertex-vision-pro' ? 'Pro' : modelTier === 'google-vertex-vision-flash' ? 'Flash' : 'Standard'}
                  </span>
                  {isSelected && <FaCheckCircle className="bounty-selected-icon" />}
                </div>
                <div className="bounty-model-characteristics">
                  <div className="bounty-characteristic">
                    <span className="label">Speed:</span>
                    <span className={`value speed-${model.speed?.toLowerCase()}`}>
                      {model.speed}
                    </span>
                  </div>
                  <div className="bounty-characteristic">
                    <span className="label">Accuracy:</span>
                    <span className={`value accuracy-${model.accuracy?.toLowerCase()}`}>
                      {model.accuracy}
                    </span>
                  </div>
                </div>
                <div className="bounty-model-best-for">
                  <span className="bounty-best-for-label">Best for:</span>
                  <div className="bounty-best-for-tags">
                    {model.bestFor?.map((use, idx) => (
                      <span key={idx} className="bounty-best-for-tag">{use}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bounty Details */}
      <div className="config-section">
        <h4>
          <FaDollarSign />
          Bounty Details
        </h4>
        <div className="form-grid">
          <div className="form-group">
            <label>Bounty Amount ($)</label>
            <input
              type="number"
              step="0.01"
              min="1"
              value={bountyAmount}
              onChange={(e) => setBountyAmount(e.target.value)}
              placeholder="Enter bounty amount"
            />
          </div>
          <div className="form-group">
            <label>Deadline (Optional)</label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="config-footer">
        <div className="cost-breakdown">
          <div className="cost-summary">
            <span className="cost-label">Estimated Analysis Cost:</span>
            <span className="cost-value">${costEstimate.toFixed(4)}</span>
          </div>
          <div className="cost-confidence">
            {selectedTargets.length} target{selectedTargets.length !== 1 ? 's' : ''} selected
          </div>
        </div>
        <button
          onClick={createBountyRequest}
          disabled={!bountyAmount || selectedTargets.length === 0 || isSubmitting}
          className="launch-bounty-btn"
        >
          <FaBullhorn className={`btn-icon ${isSubmitting ? 'spinning' : ''}`} />
          {isSubmitting ? 'Creating Bounty...' : 'Post Bounty Request'}
        </button>
      </div>
    </div>
  );
};

export default BountyConfigurationMenu;
